import { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useQuery, useQueryClient } from "@tanstack/react-query";

// Third party
import moment from "moment";

// API's
import { getOnlineRatings } from "@Dashboard/api/queries";

// Helpers
import { filterStoreRatings } from "@Dashboard/helpers/ratingBrands";
import { and } from "@Common/helpers/sonar";

// Hooks
import { useSubscriptions } from "@/Common/context/subscriptions/Subscriptions";
import { useCommissionCyclesData } from "@Washouts/hooks/common/useCommissionCyclesData";

// API
import { onCreateSocialMediaReviews } from "@DataPages/api/socialMediaReviews";

// Components
import DashboardTabs from "../DashboardTabs";
import OnlineRatingsData from "./OnlineRatingsData";

// Types
import { RootState } from "@/redux/store";
import { IReviewData, IReviewSiteSummary, ISitesReviews } from "@DataPages/types/socialMediaReviews/model";
import { IRatingConfig } from "@Dashboard/components/OnlineRatings/interfaces";
import { GraphqlSubscriptionResponse } from "@Common/api/types/GraphqlSubscriptionResponse";
import { TimePeriodSelector } from "./TimePeriodSelector";
import { Grid } from "@mui/material";

// Constants
import { CRM_KEYS } from "@/DataPages/components/CRM/constants";

/********************************************* HELPERS ************************************************/
function getDataForTimePeriod(data: ISitesReviews, timePeriod: keyof ISitesReviews): IReviewSiteSummary[] {
	if (!data) {
		return [];
	}
	return data[timePeriod];
}

export default function OnlineRatings() {
	const locationId = useSelector((state: RootState) => state.location.location?.locationId ?? "");
	const [selectedIndex, setSelectedIndex] = useState(0);

	const { addSubscription, clearSubscriptionContext, getSubscription } = useSubscriptions();
	const { commissionCycle } = useCommissionCyclesData({ date: moment().toISOString() });
	const commissionCycleId = commissionCycle?._id;

	const queryClient = useQueryClient();

	const timePeriods: Array<keyof ISitesReviews> = ["monthToDate", "lastMonth", "lastThreeMonths", "yearToDate", "allTime"];

	/********************************************* QUERIES ************************************************/
	const { data, isLoading } = useQuery({
		queryKey: ["reviewsDashboards", locationId],
		queryFn: async () => getOnlineRatings(locationId, { store: true }),
		select: (reviews) => {
			if (!reviews) {
				return null;
			}
			const selectedReview = reviews;
			const storeReviews = selectedReview.storeReviews;

			const transformedStoreReviews: { [key: string]: IRatingConfig[] } = {
				monthToDate: [],
				lastMonth: [],
				lastThreeMonths: [],
				yearToDate: [],
				allTime: [],
			};

			timePeriods.forEach((timePeriod) => {
				const timePeriodData = getDataForTimePeriod(storeReviews, timePeriod);
				transformedStoreReviews[timePeriod] = filterStoreRatings(timePeriodData);
			});

			return {
				...selectedReview,
				storeReviews: transformedStoreReviews,
			};
		},
		enabled: and(Boolean(locationId), Boolean(commissionCycleId)),
	});

	/********************************************* SUBSCRIPTIONS ************************************************/
	const localSubscription = getSubscription([CRM_KEYS.SUBSCRIPTION.SOCIAL_MEDIA_REVIEW, locationId]);
	localSubscription?.subscriptions?.onCreateSocialMediaReviews?.setCallback(
		(response: GraphqlSubscriptionResponse<"onCreateSocialMediaReviews", IReviewData>) => {
			const updatedReviews = response?.data?.onCreateSocialMediaReviews;
			queryClient.setQueryData(["reviewsDashboards", locationId], (cachedData: IReviewData) => {
				return {
					...cachedData,
					storeReviews: updatedReviews?.storeReviews,
				};
			});
		}
	);

	const tabsData = useMemo(() => {
		return ["Current Month", "Last Month", "Last 3 Months", "Year to Date", "All Time"].map((tabLabel) => {
			const tabContent = (
				<OnlineRatingsData data={data?.storeReviews?.[timePeriods[selectedIndex]]} isLoading={isLoading || !locationId} />
			);
			return { tabLabel, tabContent };
		});
	}, [data, isLoading, locationId, selectedIndex]);

	const dataTabs = tabsData?.map((el, index) => {
		return {
			...el,
			tabLabel: "",
			tabKey: el.tabLabel ?? `${index}`,
		};
	});
	const timePeriodOptions = tabsData?.map((tab) => tab.tabLabel);

	useEffect(() => {
		if (locationId) {
			addSubscription(
				["socialMediaReviews", locationId],
				[
					{
						context: "onCreateSocialMediaReviews",
						query: onCreateSocialMediaReviews,
						variables: {
							commissionCycleId,
						},
					},
				]
			);
		}

		return () => {
			clearSubscriptionContext();
		};
	}, [addSubscription, clearSubscriptionContext, locationId]);

	return (
		<DashboardTabs
			tabsIdentifier={"ratings"}
			data={dataTabs}
			tabStyles={{
				fontSize: "16px",
			}}
			extraHeaderContent={
				<Grid container sx={{ width: "400px" }}>
					<TimePeriodSelector data={timePeriodOptions} selectedIndex={selectedIndex} setSelectedIndex={setSelectedIndex} />
				</Grid>
			}
		/>
	);
}
